/**
 * Training Record Detail Page - View training details and user's participation
 */

import { useState, useRef, useMemo } from 'react'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { learningApi } from '../api/learningApi'
import type { TrainingType, VerificationStatus } from '../types/learning.types'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const TRAINING_TYPE_LABELS: Record<TrainingType, string> = {
  course: 'Course',
  workshop: 'Workshop',
  certification: 'Certification',
  webinar: 'Webinar',
  seminar: 'Seminar',
  conference: 'Conference',
  other: 'Other',
}

const TRAINING_TYPE_COLORS: Record<TrainingType, string> = {
  course: 'bg-red-100 text-red-700',
  workshop: 'bg-rose-100 text-rose-700',
  certification: 'bg-green-100 text-green-700',
  webinar: 'bg-orange-100 text-orange-700',
  seminar: 'bg-pink-100 text-pink-700',
  conference: 'bg-red-100 text-red-700',
  other: 'bg-gray-100 text-gray-700',
}

const STATUS_COLORS: Record<VerificationStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  verified: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
}

const STATUS_LABELS: Record<VerificationStatus, string> = {
  pending: 'Pending Verification',
  verified: 'Verified',
  rejected: 'Rejected',
}

const TrainingRecordDetailPage = () => {
  const { recordId } = useParams<{ recordId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [editingCertDates, setEditingCertDates] = useState(false)
  const [certDateInput, setCertDateInput] = useState('')
  const [certExpiryInput, setCertExpiryInput] = useState('')
  const [editingReview, setEditingReview] = useState(false)
  const [reviewRating, setReviewRating] = useState(0)
  const [reviewFeedback, setReviewFeedback] = useState('')
  const [hoverRating, setHoverRating] = useState(0)
  const { user } = useAuth()

  // Check if user is admin or HR (by role or division)
  const isAdminOrHR = user?.roles?.some(r => r.name === 'admin' || r.name === 'hr') ||
    user?.division_name?.toLowerCase() === 'human resources'

  // Check if came from all-records page (via referrer state or URL)
  const cameFromAllRecords = useMemo(() => {
    // Check location state first (if navigated via Link with state)
    if (location.state?.from === 'all-records') return true
    // Fallback: check if referrer contains 'all-records'
    if (document.referrer.includes('/learning/all-records')) return true
    return false
  }, [location.state])

  // Fetch record details
  const { data: record, isLoading } = useQuery({
    queryKey: ['trainingRecord', recordId],
    queryFn: () => learningApi.getRecord(recordId!),
    enabled: !!recordId,
  })

  // Fetch training stats (participant count, distribution)
  const { data: stats } = useQuery({
    queryKey: ['trainingStats', record?.training.id],
    queryFn: () => learningApi.getTrainingStats(record!.training.id),
    enabled: !!record?.training.id,
  })

  // Fetch reviews to compute a live average (same approach as TrainingDetailPage)
  const { data: reviewsData } = useQuery({
    queryKey: ['trainingReviews', record?.training.id],
    queryFn: () => learningApi.getTrainingReviews(record!.training.id, 1, 50),
    enabled: !!record?.training.id,
  })

  // Fetch certificates
  const { data: certificatesData } = useQuery({
    queryKey: ['trainingCertificates', recordId],
    queryFn: () => learningApi.listCertificates(recordId!),
    enabled: !!recordId,
  })

  const certificates = certificatesData?.data || []

  // Only the record owner can edit the review
  const isOwnRecord = !record || record.employee?.id === user?.id

  // Compute live average, always using record.rating as the owner's ground truth.
  // This corrects for cases where _sync_training_review lags behind the record update.
  const allReviews = reviewsData?.data || []
  const recordOwnerId = record?.employee?.id
  const otherReviews = allReviews.filter(r => r.employee?.id !== recordOwnerId)
  const ownerContribution = record?.rating ? [{ rating: record.rating }] : []
  const effectiveReviews = [...otherReviews, ...ownerContribution]
  const liveAvgRating = effectiveReviews.length > 0
    ? Math.round((effectiveReviews.reduce((sum, r) => sum + r.rating, 0) / effectiveReviews.length) * 10) / 10
    : null
  const liveParticipantCount = effectiveReviews.length || stats?.participant_count || 0

  // Upload certificate mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => learningApi.uploadCertificate(recordId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRecord', recordId] })
      queryClient.invalidateQueries({ queryKey: ['trainingCertificates', recordId] })
      toast.success('Certificate uploaded successfully')
      setIsUploading(false)
    },
    onError: () => {
      toast.error('Failed to upload certificate')
      setIsUploading(false)
    },
  })

  // Delete certificate mutation
  const deleteCertMutation = useMutation({
    mutationFn: (certificateId: string) => learningApi.deleteCertificate(certificateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingCertificates', recordId] })
      queryClient.invalidateQueries({ queryKey: ['trainingRecord', recordId] })
      toast.success('Certificate deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete certificate')
    },
  })

  // Verify/reject mutation (for HR/Admin)
  const verifyMutation = useMutation({
    mutationFn: ({ action, rejection_reason }: { action: 'verify' | 'reject'; rejection_reason?: string }) =>
      learningApi.verifyRecord(recordId!, action, rejection_reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['trainingRecord', recordId] })
      queryClient.invalidateQueries({ queryKey: ['allTrainingRecords'] })
      toast.success(`Training record ${variables.action === 'verify' ? 'verified' : 'rejected'} successfully`)
      setShowRejectModal(false)
      setRejectionReason('')
    },
    onError: () => {
      toast.error('Failed to update verification status')
    },
  })

  // Update certification dates mutation
  const updateCertDatesMutation = useMutation({
    mutationFn: (data: { certification_date: string; certification_expiry_date?: string }) =>
      learningApi.updateRecord(recordId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRecord', recordId] })
      queryClient.invalidateQueries({ queryKey: ['myCertifications'] })
      toast.success('Certification dates updated')
      setEditingCertDates(false)
    },
    onError: (error: any) => {
      // API client unwraps error.response.data before rejecting.
      // FastAPI 422: { detail: [{ msg: '...' }] } or { detail: 'string' }
      // HRMS custom errors: { error: { message: '...' } }
      const detail = error?.detail
      const msg = (Array.isArray(detail) ? detail[0]?.msg?.replace(/^Value error,\s*/i, '') : null)
        ?? (typeof detail === 'string' ? detail : null)
        ?? error?.error?.message
        ?? 'Failed to update certification dates'
      toast.error(msg)
    },
  })

  // Update review (rating + feedback) mutation
  const updateReviewMutation = useMutation({
    mutationFn: (data: { rating: number; feedback: string }) =>
      learningApi.updateRecord(recordId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingRecord', recordId] })
      queryClient.invalidateQueries({ queryKey: ['trainingStats', record?.training.id] })
      queryClient.invalidateQueries({ queryKey: ['trainingReviews', record?.training.id] })
      queryClient.invalidateQueries({ queryKey: ['myTrainingRecords'] })
      toast.success('Review saved')
      setEditingReview(false)
    },
    onError: () => {
      toast.error('Failed to save review')
    },
  })

  const handleSaveReview = () => {
    if (reviewRating < 1) {
      toast.error('Please select a rating')
      return
    }
    updateReviewMutation.mutate({ rating: reviewRating, feedback: reviewFeedback })
  }

  const handleStartEditReview = () => {
    setReviewRating(record?.rating ?? 0)
    setReviewFeedback(record?.feedback ?? '')
    setEditingReview(true)
  }

  const handleSaveCertDates = () => {
    if (!certDateInput) {
      toast.error('Please enter the issue date')
      return
    }
    if (certExpiryInput && certExpiryInput <= certDateInput) {
      toast.error('Expiry date must be after the issue date')
      return
    }
    updateCertDatesMutation.mutate({
      certification_date: certDateInput,
      certification_expiry_date: certExpiryInput || undefined,
    })
  }

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection')
      return
    }
    verifyMutation.mutate({ action: 'reject', rejection_reason: rejectionReason })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setIsUploading(true)
      uploadMutation.mutate(file)
    }
  }

  const renderStars = (rating: number | undefined, size: string = 'w-5 h-5') => {
    if (!rating) return null
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${size} ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  if (!record) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Record not found</h3>
          <p className="text-gray-500 mb-6">The training record you're looking for doesn't exist.</p>
          <Link
            to="/learning/my-records"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to My Records
          </Link>
        </div>
      </div>
    )
  }

  const training = record.training

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/learning" className="hover:text-white transition-colors">Learning</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {isAdminOrHR && (cameFromAllRecords || record.employee?.id !== user?.id) ? (
              <Link to="/learning/all-records" className="hover:text-white transition-colors">All Records</Link>
            ) : (
              <Link to="/learning/my-records" className="hover:text-white transition-colors">My Records</Link>
            )}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white truncate max-w-xs">{training.name}</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${TRAINING_TYPE_COLORS[training.training_type]}`}>
                  {TRAINING_TYPE_LABELS[training.training_type]}
                </span>
                {training.category && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-white/20 text-white">
                    {training.category.name}
                  </span>
                )}
                {training.deleted_at && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-500 text-white flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Training Deleted
                  </span>
                )}
                {!training.deleted_at && training.is_active === false && (
                  <span className="px-3 py-1 text-sm font-medium rounded-full bg-gray-500 text-white flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    Training Deactivated
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">{training.name}</h1>
              {training.provider && (
                <p className="text-red-200 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {training.provider}
                </p>
              )}
            </div>
            <div className={`px-4 py-2 rounded-xl border ${STATUS_COLORS[record.verification_status]}`}>
              <div className="flex items-center gap-2">
                {record.verification_status === 'verified' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )}
                {record.verification_status === 'pending' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                {record.verification_status === 'rejected' && (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
                <span className="font-medium">{STATUS_LABELS[record.verification_status]}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* My Participation */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                My Participation
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">Start Date</p>
                  <p className="font-semibold text-gray-900">
                    {format(new Date(record.start_date), 'MMMM d, yyyy')}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500 mb-1">End Date</p>
                  <p className="font-semibold text-gray-900">
                    {record.end_date ? format(new Date(record.end_date), 'MMMM d, yyyy') : 'Ongoing'}
                  </p>
                </div>
              </div>

              {/* Review Section */}
              <div className="mt-4 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    My Review
                  </p>
                  {isOwnRecord && !editingReview && (
                    <button
                      onClick={handleStartEditReview}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      {record.rating ? 'Edit' : 'Add Review'}
                    </button>
                  )}
                </div>

                {editingReview ? (
                  <div className="space-y-4">
                    {/* Star selector */}
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Rating <span className="text-red-500">*</span></label>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewRating(star)}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="focus:outline-none"
                          >
                            <svg
                              className={`w-9 h-9 transition-colors ${star <= (hoverRating || reviewRating) ? 'text-yellow-400' : 'text-gray-200 hover:text-yellow-200'}`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          </button>
                        ))}
                        {reviewRating > 0 && (
                          <span className="ml-2 text-sm text-gray-500">{reviewRating}/5</span>
                        )}
                      </div>
                    </div>
                    {/* Feedback textarea */}
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Feedback <span className="text-gray-400">(optional)</span></label>
                      <textarea
                        value={reviewFeedback}
                        onChange={e => setReviewFeedback(e.target.value)}
                        rows={3}
                        placeholder="Share your thoughts about this training..."
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleSaveReview}
                        disabled={updateReviewMutation.isPending || reviewRating < 1}
                        className="px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {updateReviewMutation.isPending ? 'Saving...' : 'Save Review'}
                      </button>
                      <button
                        onClick={() => setEditingReview(false)}
                        disabled={updateReviewMutation.isPending}
                        className="px-5 py-2 text-gray-600 text-sm font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : record.rating ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      {renderStars(record.rating, 'w-6 h-6')}
                      <span className="font-semibold text-gray-900">{record.rating}/5</span>
                    </div>
                    {record.feedback && (
                      <p className="text-sm text-gray-700 bg-gray-50 rounded-lg px-4 py-3 leading-relaxed">{record.feedback}</p>
                    )}
                  </div>
                ) : isOwnRecord ? (
                  <button
                    onClick={handleStartEditReview}
                    className="w-full py-4 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50/50 transition-all text-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add your review
                  </button>
                ) : (
                  <p className="text-sm text-gray-400 italic">No review submitted</p>
                )}
              </div>

              {/* Rejection Reason */}
              {record.verification_status === 'rejected' && record.rejection_reason && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-sm text-red-600 font-medium mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    Rejection Reason
                  </p>
                  <p className="text-red-700">{record.rejection_reason}</p>
                </div>
              )}
            </div>

            {/* Certification Details - only for certification type */}
            {training.training_type === 'certification' && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    Certification Details
                  </h2>
                  {!editingCertDates && (
                    <button
                      onClick={() => {
                        setCertDateInput(record.certification_date ?? '')
                        setCertExpiryInput(record.certification_expiry_date ?? '')
                        setEditingCertDates(true)
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                  )}
                </div>

                {editingCertDates ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Issue Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={certDateInput}
                          onChange={e => setCertDateInput(e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-400">Date the certification was issued</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          value={certExpiryInput}
                          onChange={e => setCertExpiryInput(e.target.value)}
                          min={certDateInput
                            ? new Date(new Date(certDateInput + 'T00:00:00').getTime() + 86400000).toISOString().split('T')[0]
                            : undefined}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        />
                        <p className="mt-1 text-xs text-gray-400">Must be after issue date. Leave blank if it does not expire.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-1">
                      <button
                        onClick={handleSaveCertDates}
                        disabled={updateCertDatesMutation.isPending}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {updateCertDatesMutation.isPending ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingCertDates(false)}
                        className="px-4 py-2 text-gray-600 text-sm font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Issue Date</p>
                      <p className="font-semibold text-gray-900">
                        {record.certification_date
                          ? format(new Date(record.certification_date), 'MMMM d, yyyy')
                          : <span className="text-gray-400 font-normal">Not set</span>}
                      </p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500 mb-1">Expiry Date</p>
                      <p className="font-semibold text-gray-900">
                        {record.certification_expiry_date
                          ? format(new Date(record.certification_expiry_date), 'MMMM d, yyyy')
                          : <span className="text-gray-400 font-normal">No expiry</span>}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Certificate Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  Certificates
                  {certificates.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                      {certificates.length}
                    </span>
                  )}
                </h2>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {isUploading ? 'Uploading...' : 'Add Certificate'}
                </button>
              </div>

              {certificates.length > 0 ? (
                <div className="space-y-3">
                  {certificates.map((cert) => (
                    <div
                      key={cert.id}
                      className="p-4 bg-green-50 border border-green-200 rounded-xl"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">{cert.original_filename}</p>
                            <p className="text-xs text-gray-500">
                              {cert.file_size ? `${(cert.file_size / 1024).toFixed(1)} KB` : ''}
                              {cert.uploaded_at && ` • Uploaded ${format(new Date(cert.uploaded_at), 'MMM d, yyyy')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={async () => {
                              try {
                                const blob = await learningApi.downloadCertificateById(cert.id)
                                const url = window.URL.createObjectURL(blob)
                                window.open(url, '_blank')
                              } catch {
                                toast.error('Failed to open certificate')
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="View"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                const blob = await learningApi.downloadCertificateById(cert.id)
                                const url = window.URL.createObjectURL(blob)
                                const a = document.createElement('a')
                                a.href = url
                                a.download = cert.original_filename
                                document.body.appendChild(a)
                                a.click()
                                document.body.removeChild(a)
                                window.URL.revokeObjectURL(url)
                              } catch {
                                toast.error('Failed to download certificate')
                              }
                            }}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Download"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Are you sure you want to delete this certificate?')) {
                                deleteCertMutation.mutate(cert.id)
                              }
                            }}
                            disabled={deleteCertMutation.isPending}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-red-300 hover:bg-red-50/50 transition-all"
                >
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-600 mb-1">
                    {isUploading ? 'Uploading...' : 'Click to upload certificate'}
                  </p>
                  <p className="text-sm text-gray-400">PDF, JPG, or PNG (max 5MB)</p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Training Description */}
            {training.category && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">About this Training</h2>
                <p className="text-gray-600">
                  This {TRAINING_TYPE_LABELS[training.training_type].toLowerCase()} is part of the {training.category.name} category.
                  {training.provider && ` Provided by ${training.provider}.`}
                </p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* HR Actions - Verify/Reject for pending records */}
            {isAdminOrHR && record.verification_status === 'pending' && (
              <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                <h3 className="text-sm font-medium text-amber-800 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Verification Required
                </h3>
                <p className="text-sm text-amber-700 mb-4">
                  Review this training record and verify or reject the submission.
                </p>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => verifyMutation.mutate({ action: 'verify' })}
                    disabled={verifyMutation.isPending}
                    className="w-full px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Verify Record
                  </button>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    disabled={verifyMutation.isPending}
                    className="w-full px-4 py-2.5 bg-white text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 border border-red-200"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject Record
                  </button>
                </div>
              </div>
            )}

            {/* Training Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">Training Statistics</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Participants</span>
                  <span className="font-semibold text-gray-900">{liveParticipantCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Average Rating</span>
                  <div className="flex items-center gap-1">
                    {liveAvgRating ? (
                      <>
                        <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="font-semibold text-gray-900">{liveAvgRating.toFixed(1)}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">No ratings</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Record Created</span>
                  <span className="font-semibold text-gray-900">
                    {format(new Date(record.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            </div>

            {/* View Training Repository Link */}
            <div className="bg-red-50 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-red-800 uppercase tracking-wide mb-3">Quick Actions</h3>
              <Link
                to={`/learning/trainings/${training.id}`}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-red-700 font-medium rounded-xl hover:bg-red-100 transition-colors border border-red-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View in Repository
              </Link>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8">
          <button
            onClick={() => navigate(
              isAdminOrHR && (cameFromAllRecords || record.employee?.id !== user?.id)
                ? '/learning/all-records'
                : '/learning/my-records'
            )}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {isAdminOrHR && (cameFromAllRecords || record.employee?.id !== user?.id)
              ? 'Back to All Records'
              : 'Back to My Records'}
          </button>
        </div>
      </div>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reject Training Record</h3>
                <p className="text-sm text-gray-500">Please provide a reason for rejection</p>
              </div>
            </div>

            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter the reason for rejecting this training record..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
              autoFocus
            />

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectionReason('')
                }}
                className="flex-1 px-4 py-2.5 text-gray-700 font-medium bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={verifyMutation.isPending || !rejectionReason.trim()}
                className="flex-1 px-4 py-2.5 text-white font-medium bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {verifyMutation.isPending ? 'Rejecting...' : 'Reject Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TrainingRecordDetailPage
